import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { Application, ApplicationStatusEnum } from '@/types/application'

interface SameCompanyApplication {
  id: number
  open_role: {
    role_name: string
    url: string
  }
  updated_at: string
  application_status?: string
}

interface EmployerDetails {
  id: number
  name: string
  url?: string
  outtalent_internal_notes?: string
}

interface ReferrerDetails {
  id: number
  name: string
  email?: string
  notes?: string
}

/** PATCH payload for Kanban drag-and-drop */
export type KanbanCardPatch = {
  application_status: string
  interview_status?: string | null
}

/** POST /job_applications/applications/ (create from existing open role) */
export type CreateApplicationPayload = { open_role: number }

interface EmailResponse {
  is_valid_email: boolean
  missing_fields: string[]
  template_name: string
  html_content: string
  text_content: string
  subject: string
  to_email: string
  to_name?: string
  cc_list: string[]
  same_company_applications?: SameCompanyApplication[]
  employer_details?: EmployerDetails
  referrer_details?: ReferrerDetails
  updated_at?: string
  created_at?: string
}

interface FetchParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
  status?: string;
  application_status?: string;
  referral_status?: string;
  referral_type?: string;
  interview_status?: string;
  user_role?: string;
  employer?: string;
}

interface PrivateJobApplicationData {
  role_name: string;
  url: string;
  seniority_level: string;
  employer_id: number;
  work_authorization_requirement: string;
  qualifications: string;
  description: string;
  country_id?: number;
  city_id?: number;
  is_remote?: boolean;
  region?: string;
}

export const useApplicationsStore = defineStore('applications', () => {
  const applications = ref<Application[]>([])
  const userApplications = ref<Application[]>([]) // New state for user-specific applications
  const kanbanApplications = ref<Application[]>([]) // All applications for Kanban view (unpaginated)
  const isLoadingKanban = ref(false)
  const selectedApplication = ref<Application | null>(null)
  const isLoadingList = ref(false)
  const isLoadingSelected = ref(false)
  const error = ref<string | null>(null)

  // Add pagination state
  const totalCount = ref(0)
  const userTotalCount = ref(0) // New total count for user applications
  const currentPage = ref(1)
  const pageSize = ref(50)
  const lastRequestedPage = ref<number | null>(null) // Track last requested page

  const emailPreview = ref<EmailResponse | null>(null)
  const emailError = ref<string | null>(null)

  const searchQuery = ref('')
  const selectedStatus = ref('')
  const selectedApplicationStatus = ref('')
  const selectedReferralStatus = ref('')
  const selectedReferralType = ref('')
  const selectedInterviewStatus = ref('')
  const selectedEmployer = ref('')
  const currentSort = ref('-updated_at')
  const selectedUserRole = ref('fellow') // Default to fellow


  const fetchUserApplications = async (page = 1, size = pageSize.value) => {
    // Convert page to number and ensure it's valid
    const pageNum = parseInt(String(page));
    const validPage = isNaN(pageNum) ? 1 : pageNum;

    // If requesting page 1, reset the last requested page (new filter/search)
    if (validPage === 1) {
      lastRequestedPage.value = null;
    }

    // Prevent duplicate requests for the same page
    if (isLoadingList.value || lastRequestedPage.value === validPage) {
      return;
    }

    isLoadingList.value = true;
    error.value = null;
    // Track this request
    lastRequestedPage.value = validPage;

    try {
      // Use new filter system like fetchApplications
      const params = {
        page: validPage,
        page_size: size,
        ordering: currentSort.value,
        ...(searchQuery.value && { search: searchQuery.value }),
        ...(selectedEmployer.value && { employer: selectedEmployer.value }),
        ...(selectedApplicationStatus.value && { application_status: selectedApplicationStatus.value }),
        ...(selectedReferralStatus.value && { referral_status: selectedReferralStatus.value }),
        ...(selectedReferralType.value && { referral_type: selectedReferralType.value }),
        ...(selectedInterviewStatus.value && { interview_status: selectedInterviewStatus.value })
      }

      // For debugging
      const response = await api.get(API_ROUTES.GET_USER_APPLICATIONS, {
        params
      })

      // Store in userApplications instead of applications
      userApplications.value = response.data.results || []
      userTotalCount.value = response.data.count

      // Copy to applications for backward compatibility
      applications.value = userApplications.value
      totalCount.value = userTotalCount.value

      // Update currentPage with the page we actually requested to ensure consistency
      currentPage.value = validPage

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch applications'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  /**
   * Optimistically move a card on the Kanban board (application_status; optional interview_status).
   * Reverts the local change if the PATCH request fails.
   */
  const moveKanbanCard = async (appId: number, patch: KanbanCardPatch): Promise<void> => {
    const idx = kanbanApplications.value.findIndex((a) => a.id === appId)
    if (idx === -1) return

    const previousRow = { ...kanbanApplications.value[idx] }
    const nextFields = {
      application_status: patch.application_status,
      ...(patch.interview_status !== undefined
        ? { interview_status: patch.interview_status as Application['interview_status'] }
        : {})
    } as const

    kanbanApplications.value[idx] = {
      ...kanbanApplications.value[idx],
      ...nextFields
    }

    const selectedIsSame =
      selectedApplication.value != null && selectedApplication.value.id === appId
    const previousSelected = selectedIsSame ? { ...selectedApplication.value! } : null
    if (selectedIsSame) {
      selectedApplication.value = {
        ...selectedApplication.value!,
        ...nextFields
      }
    }

    const body: Record<string, string | null> = {
      application_status: patch.application_status
    }
    if (patch.interview_status !== undefined) {
      body.interview_status = patch.interview_status
    }

    try {
      await api.patch(API_ROUTES.UPDATE_USER_APPLICATION(String(appId)), body)
    } catch {
      kanbanApplications.value[idx] = previousRow
      if (previousSelected) {
        selectedApplication.value = previousSelected
      }
      throw new Error('Failed to update application status')
    }
  }

  /**
   * Fetch ALL of the current user's applications without pagination, for the Kanban view.
   * Respects the same search / employer filters as fetchUserApplications.
   */
  const fetchApplicationsForKanban = async () => {
    if (isLoadingKanban.value) return
    isLoadingKanban.value = true
    error.value = null

    try {
      const params: Record<string, string> = { kanban: 'true' }

      if (searchQuery.value?.trim()) params.search = searchQuery.value.trim()
      if (selectedEmployer.value) params.employer = selectedEmployer.value
      if (selectedApplicationStatus.value) params.application_status = selectedApplicationStatus.value
      if (selectedInterviewStatus.value) params.interview_status = selectedInterviewStatus.value
      if (selectedReferralStatus.value) params.referral_status = selectedReferralStatus.value
      if (selectedReferralType.value) params.referral_type = selectedReferralType.value

      const response = await api.get(API_ROUTES.GET_USER_APPLICATIONS, { params })
      kanbanApplications.value = response.data.results ?? []
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch applications for Kanban'
      throw err
    } finally {
      isLoadingKanban.value = false
    }
  }

  const fetchApplicationById = async (id: string) => {
    isLoadingSelected.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_USER_APPLICATION_BY_ID(id))
      selectedApplication.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch application'
      throw err
    } finally {
      isLoadingSelected.value = false
    }
  }

  const updateApplication = async (id: string, data: Partial<Application>, background = false) => {
    if (!background) {
      isLoadingSelected.value = true
    }
    error.value = null

    try {
      const response = await api.patch<{ id: number; updated_at: string }>(
        API_ROUTES.UPDATE_USER_APPLICATION(id),
        data
      )

      if (selectedApplication.value?.id === Number(id)) {
        // Merge request payload (server may echo only id + updated_at on PATCH)
        Object.assign(selectedApplication.value, data, response.data)
      }

      // Also merge the updated fields into the application within the lists
      const mergeIntoArray = (appArray: Application[]) => {
        const applicationIndex = appArray.findIndex((app) => app.id === Number(id))
        if (applicationIndex !== -1) {
          appArray[applicationIndex] = {
            ...appArray[applicationIndex],
            ...data,
            ...response.data
          }
        }
      }

      mergeIntoArray(applications.value)
      mergeIntoArray(userApplications.value)
      mergeIntoArray(kanbanApplications.value)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update application'
      throw err
    } finally {
      if (!background) {
        isLoadingSelected.value = false
      }
    }
  }

  const createApplication = async (data: CreateApplicationPayload) => {
    isLoadingList.value = true
    error.value = null

    try {
      const response = await api.post<{ id: number }>(
        API_ROUTES.CREATE_USER_APPLICATION,
        data
      )
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create application'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  const createPrivateApplication = async (data: PrivateJobApplicationData) => {
    isLoadingList.value = true
    error.value = null

    try {
      const response = await api.post<{ id: number; updated_at: string }>(
        API_ROUTES.CREATE_PRIVATE_JOB_APPLICATION,
        data
      )
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create private application'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  const deleteApplication = async (id: string) => {
    isLoadingList.value = true
    error.value = null

    try {
      await api.delete(API_ROUTES.DELETE_USER_APPLICATION(id))
      // Remove the deleted application from the local state
      applications.value = applications.value.filter((app) => app.id !== Number(id))
      userApplications.value = userApplications.value.filter((app) => app.id !== Number(id))

      if (selectedApplication.value?.id === Number(id)) {
        selectedApplication.value = null
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete application'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  const updateApplicationStatus = async (id: string, status: string) => {
    try {
      const response = await api.post(API_ROUTES.UPDATE_USER_APPLICATION_STATUS(id), {
        status: status as ApplicationStatusEnum
      })

      // Update selected application if it matches
      if (selectedApplication.value?.id === Number(id)) {
        selectedApplication.value = {
          ...selectedApplication.value,
          status: status as ApplicationStatusEnum
        }
      }

      // Update the application in both application arrays
      const updateInArray = (appArray: Application[]) => {
        const applicationIndex = appArray.findIndex((app) => app.id === Number(id))
        if (applicationIndex !== -1) {
          appArray[applicationIndex] = {
            ...appArray[applicationIndex],
            status: status as ApplicationStatusEnum
          }
        }
      }

      updateInArray(applications.value)
      updateInArray(userApplications.value)

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update application status'
      throw err
    }
  }

  const updateApplicationComments = async (id: string, comments: string) => {
    try {
      const response = await updateApplication(id, { comments })

      // Update the application in both application arrays
      const updateInArray = (appArray: Application[]) => {
        const applicationIndex = appArray.findIndex((app) => app.id === Number(id))
        if (applicationIndex !== -1) {
          appArray[applicationIndex] = {
            ...appArray[applicationIndex],
            comments
          }
        }
      }

      updateInArray(applications.value)
      updateInArray(userApplications.value)

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update application comments'
      throw err
    }
  }

  const fetchApplicationsByUserId = async (userId: string, page = 1, pageSizeParam = pageSize.value, ordering = currentSort.value) => {
    isLoadingList.value = true
    error.value = null

    try {
      const params = {
        page,
        page_size: pageSizeParam,
        ordering,
        ...(selectedStatus.value && { status: selectedStatus.value }),
        ...(selectedApplicationStatus.value && { application_status: selectedApplicationStatus.value }),
        ...(selectedReferralStatus.value && { referral_status: selectedReferralStatus.value }),
        ...(selectedReferralType.value && { referral_type: selectedReferralType.value }),
        ...(selectedInterviewStatus.value && { interview_status: selectedInterviewStatus.value })
      }
      const response = await api.get(API_ROUTES.GET_APPLICATIONS_BY_USER_ID(userId), {
        params
      })

      // Store in userApplications
      userApplications.value = response.data.results
      userTotalCount.value = response.data.count

      // Copy to applications for backward compatibility
      applications.value = userApplications.value
      totalCount.value = userTotalCount.value

      currentPage.value = page
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch applications'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  // Track last fetched email preview to prevent duplicate calls
  const lastEmailPreviewId = ref<string | null>(null)
  const isEmailPreviewFetching = ref(false)

  const previewReferralEmail = async (id: string | string[] | number[] | number) => {
    const idString = Array.isArray(id) ? id.join(',') : id.toString()

    // Prevent duplicate calls
    if (isEmailPreviewFetching.value || idString === lastEmailPreviewId.value) {
      return emailPreview.value
    }

    isLoadingSelected.value = true
    isEmailPreviewFetching.value = true
    emailError.value = null
    lastEmailPreviewId.value = idString

    try {
      const response = await api.get(API_ROUTES.PREVIEW_REFERRAL_EMAIL(idString))
      emailPreview.value = response.data
      return response.data
    } catch (err: any) {
      emailError.value = err.response?.data?.message || 'Failed to preview email'
      lastEmailPreviewId.value = null // Reset on error so it can be retried
      throw err
    } finally {
      isLoadingSelected.value = false
      isEmailPreviewFetching.value = false
    }
  }

  const updateReferralEmailContent = async (id: string | string[] | number[] | number) => {
    emailError.value = null

    const idString = Array.isArray(id) ? id.join(',') : id.toString()

    try {
      const response = await api.get(API_ROUTES.PREVIEW_REFERRAL_EMAIL(idString))

      // Only update the content-related fields to avoid full component re-render
      if (emailPreview.value) {
        emailPreview.value.html_content = response.data.html_content
        emailPreview.value.text_content = response.data.text_content
        emailPreview.value.subject = response.data.subject
        emailPreview.value.is_valid_email = response.data.is_valid_email
        emailPreview.value.missing_fields = response.data.missing_fields
      } else {
        emailPreview.value = response.data
      }

      return response.data
    } catch (err: any) {
      emailError.value = err.response?.data?.message || 'Failed to update email content'
      throw err
    }
  }

  const sendReferralEmail = async (id: string | string[] | number[] | number) => {
    isLoadingSelected.value = true
    emailError.value = null

    const idString = Array.isArray(id) ? id.join(',') : id.toString()

    try {
      const response = await api.post(API_ROUTES.SEND_REFERRAL_EMAIL(idString))
      return response.data
    } catch (err: any) {
      emailError.value = err.response?.data?.message || 'Failed to send email'
      throw err
    } finally {
      isLoadingSelected.value = false
    }
  }

  const fetchApplications = async (params: FetchParams = {}) => {
    isLoadingList.value = true
    error.value = null

    try {
      const queryParams: FetchParams = {
        page: currentPage.value,
        page_size: params.page_size || pageSize.value,
        ordering: params.ordering || currentSort.value,
        ...(searchQuery.value && { search: searchQuery.value }),
        ...(selectedUserRole.value && { user_role: selectedUserRole.value }),
        ...(selectedApplicationStatus.value && { application_status: selectedApplicationStatus.value }),
        ...(selectedReferralStatus.value && { referral_status: selectedReferralStatus.value }),
        ...(selectedReferralType.value && { referral_type: selectedReferralType.value }),
        ...(selectedInterviewStatus.value && { interview_status: selectedInterviewStatus.value }),
        ...params
      }

      // Apply status filter if selected
      if (selectedStatus.value) {
        queryParams.status = selectedStatus.value;
      } else {
        // No specific status is active, remove status param if it exists from override
        delete queryParams.status;
      }

      // Ensure page is part of the queryParams passed to the API
      if (params.page) {
        queryParams.page = params.page
      }

      // Log the final query params for debugging
      const response = await api.get(API_ROUTES.GET_ALL_APPLICATIONS, {
        params: queryParams
      })

      // Only update applications, not userApplications
      applications.value = response.data.results || []
      totalCount.value = response.data.count
      currentPage.value = queryParams.page ?? currentPage.value // Ensure page is a number before assigning
      return response.data
    } catch (err: any) {
      console.error('Error fetching applications:', err)
      error.value = err.response?.data?.message || 'Failed to fetch applications'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  const updateSort = (field: string, router: any) => {
    // Toggle sort direction
    const newSort = currentSort.value === field
      ? `-${field}`
      : currentSort.value === `-${field}`
        ? field
        : field  // Default to ascending on first click

    currentSort.value = newSort
    currentPage.value = 1

    // Update URL and fetch data
    router.push({
      query: {
        ...router.currentRoute.value.query,
        ordering: newSort,
        page: '1',
        page_size: pageSize.value
      }
    })

    // Fetch with explicit parameters ensuring correct filter context
    return fetchApplications({
      ordering: newSort,
      page: 1,
      page_size: pageSize.value,
      status: selectedStatus.value || undefined,
    })
  }

  const updateSearch = (query: string, router: any) => {
    searchQuery.value = query
    currentPage.value = 1

    // Update URL with page_size and trigger fetch
    router.push({
      query: {
        ...router.currentRoute.value.query,
        search: query || undefined,
        page: '1',
        page_size: pageSize.value
      }
    }).then(() => {
      // Fetch applications after URL update, passing current filter state
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchApplications({
        status: selectedStatus.value || undefined,
      })
    })
  }

  const updateStatus = (status: string, router: any) => {
    selectedStatus.value = status
    currentPage.value = 1 // Reset to first page

    // Update URL with page_size
    router.push({
      query: {
        ...router.currentRoute.value.query,
        status: status || undefined,
        page: '1',
        page_size: pageSize.value
      }
    })
  }

  const guideEditApplication = async (id: string, data: Partial<Application>) => {
    isLoadingSelected.value = true
    error.value = null

    try {
      const response = await api.patch(API_ROUTES.GUIDE_EDIT_APPLICATION(id), data)

      if (selectedApplication.value?.id === Number(id)) {
        Object.assign(selectedApplication.value, response.data)
      }

      const mergeIntoArray = (appArray: Application[]) => {
        const idx = appArray.findIndex((app) => app.id === Number(id))
        if (idx !== -1) {
          appArray[idx] = { ...appArray[idx], ...response.data }
        }
      }

      mergeIntoArray(applications.value)
      mergeIntoArray(userApplications.value)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update application'
      throw err
    } finally {
      isLoadingSelected.value = false
    }
  }

  const matchResumeToJob = async (id: string | number, background = false) => {
    if (!background) {
      isLoadingSelected.value = true
    }
    error.value = null

    // Optimistically update match_status to PENDING
    if (selectedApplication.value?.id === Number(id)) {
      selectedApplication.value.match_status = 'PENDING'
    }

    try {
      const response = await api.post(API_ROUTES.MATCH_RESUME_TO_JOB(id))

      // Update selected application if it matches
      if (selectedApplication.value?.id === Number(id)) {
        selectedApplication.value = {
          ...selectedApplication.value,
          ...response.data
        }
      }

      // Update the application in both application arrays
      const updateInArray = (appArray: Application[]) => {
        const applicationIndex = appArray.findIndex((app) => app.id === Number(id))
        if (applicationIndex !== -1) {
          appArray[applicationIndex] = {
            ...appArray[applicationIndex],
            ...response.data
          }
        }
      }

      updateInArray(applications.value)
      updateInArray(userApplications.value)

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to match resume to job'
      throw err
    } finally {
      if (!background) {
        isLoadingSelected.value = false
      }
    }
  }

  return {
    applications,
    userApplications,
    kanbanApplications,
    isLoadingKanban,
    selectedApplication,
    isLoadingList,
    isLoadingSelected,
    error,
    totalCount,
    userTotalCount,
    currentPage,
    pageSize,
    lastRequestedPage,
    fetchUserApplications,
    fetchApplicationsForKanban,
    moveKanbanCard,
    fetchApplicationById,
    updateApplication,
    updateApplicationStatus,
    updateApplicationComments,
    createApplication,
    createPrivateApplication,
    deleteApplication,
    fetchApplicationsByUserId,
    emailPreview,
    emailError,
    previewReferralEmail,
    updateReferralEmailContent,
    sendReferralEmail,
    searchQuery,
    selectedStatus,
    selectedApplicationStatus,
    selectedReferralStatus,
    selectedReferralType,
    selectedInterviewStatus,
    selectedEmployer,
    currentSort,
    selectedUserRole,
    fetchApplications,
    updateSort,
    updateSearch,
    updateStatus,
    matchResumeToJob,
    guideEditApplication
  }
})
